import { useState, useEffect, useCallback } from "react";
import { userApi } from "configs/api";

interface Branch {
  _id: string;
  name: string;
  email?: string;
}

interface UseBranchFilterProps {
  isAdmin: boolean;
  isCompanyAdmin: boolean;
  isFellowAdmin: boolean;
  isCompany: boolean;
  isManagerOrHR: boolean;
  user: any;
  selectedCompanyId: string;
}

interface UseBranchFilterReturn {
  branches: Branch[];
  loadingBranches: boolean;
  selectedBranchId: string;
  setSelectedBranchId: (id: string) => void;
  shouldShowBranchFilter: boolean;
}

/**
 * Custom hook to manage branch filtering logic.
 * Handles visibility rules, branch fetching, and state management.
 * Follows Single Responsibility Principle - encapsulates all branch filter logic.
 */
export const useBranchFilter = ({
  isAdmin,
  isCompanyAdmin,
  isFellowAdmin,
  isCompany,
  isManagerOrHR,
  user,
  selectedCompanyId,
}: UseBranchFilterProps): UseBranchFilterReturn => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");

  const normalizeId = (value: any): string => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      return value._id || value.id || "";
    }
    return "";
  };

  const resolvedBranchId = normalizeId(user?.branchID || user?.BranchID);
  const resolvedCompanyId = normalizeId(user?.companyID || user?.CompanyID);
  const resolvedUserId = normalizeId(user?._id || user?.userId);

  /**
   * Determines if branch filter should be visible based on role and selections.
   * Admin roles: Show only when company is selected
   * Company/Manager/HR: Show always
   */
  const shouldShowBranchFilter = useCallback((): boolean => {
    if (isAdmin || isCompanyAdmin || isFellowAdmin) {
      return Boolean(selectedCompanyId);
    }
    // Manager/HR should only see this filter when they are company-scoped (branchID is null).
    if (isManagerOrHR) return !resolvedBranchId;
    return isCompany;
  }, [isAdmin, isCompanyAdmin, isFellowAdmin, isCompany, isManagerOrHR, selectedCompanyId, resolvedBranchId]);

  /**
   * Fetches branches based on role and selections.
   * Respects scope rules:
   * - Admin roles: Fetch from selected company
   * - Company: Fetch from own company
   * - Manager/HR: Fetch from own branch (or all if no branchID)
   */
  const fetchBranches = useCallback(async () => {
    setLoadingBranches(true);
    try {
      let companyID: string | undefined;

      if (isAdmin || isCompanyAdmin || isFellowAdmin) {
        if (!selectedCompanyId) {
          setBranches([]);
          setLoadingBranches(false);
          return;
        }
        companyID = selectedCompanyId;
      } else if (isCompany) {
        // Company account may not always carry companyID; fallback to its own _id.
        companyID = resolvedCompanyId || resolvedUserId;
      } else if (isManagerOrHR && !resolvedBranchId) {
        companyID = resolvedCompanyId;
      } else if (isManagerOrHR && resolvedBranchId) {
        // Manager/HR with branch: fetch from their branch only
        // This is handled at component level (pre-filter)
        setBranches([{ _id: resolvedBranchId, name: user?.branchName || "Your Branch" }]);
        setLoadingBranches(false);
        return;
      }

      if (!companyID) {
        setBranches([]);
        setLoadingBranches(false);
        return;
      }

      // Branches come from the dedicated branches table in compcheck via user-service.
      const response = await userApi.get("/api/users/branches", { params: { companyID } });
      if (response.data.status) {
        const branchList = response.data.data?.data || [];
        setBranches(branchList);
      } else {
        setBranches([]);
      }
    } catch (error) {
      console.error("Failed to fetch branches:", error);
      setBranches([]);
    } finally {
      setLoadingBranches(false);
    }
  }, [
    isAdmin,
    isCompanyAdmin,
    isFellowAdmin,
    isCompany,
    isManagerOrHR,
    selectedCompanyId,
    resolvedCompanyId,
    resolvedBranchId,
    resolvedUserId,
    user?.branchName,
  ]);

  /**
   * Reset branch selection when company selection changes.
   * Prevents stale data when switching companies.
   */
  useEffect(() => {
    setSelectedBranchId("");
  }, [selectedCompanyId]);

  /**
   * Fetch branches when visibility rules or selected company changes.
   */
  useEffect(() => {
    if (shouldShowBranchFilter()) {
      fetchBranches();
    } else {
      setBranches([]);
    }
  }, [shouldShowBranchFilter, fetchBranches, selectedCompanyId]);

  return {
    branches,
    loadingBranches,
    selectedBranchId,
    setSelectedBranchId,
    shouldShowBranchFilter: shouldShowBranchFilter(),
  };
};
